/**
 * useMembershipFields (ORCH-336)
 *
 * Purpose: The `user_tenant` model's fields for a tenant, with what is needed to
 *          show a membership's values by name: each field's label, and a lookup
 *          that resolves a value id at any level of a hierarchy (a Geography
 *          attribute may hold a region, a sub-region or a country).
 * Logic: Reads the same cached edit view MetadataForm renders, so labels and
 *        field order always match the form. Another model's form (the `user`
 *        profile, for History, ORCH-422) is named the same way via `modelId`.
 */

import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { metadataService } from '../../services/metadataService';
import { useMDMLookup } from '../../services/mdmCacheService';
import { useHierarchyAncestorFields } from '../../hooks/useHierarchyAncestorFields';
import { ViewConfiguration, ViewField } from '../metadata-form/types';
import { MEMBERSHIP_MODEL_ID, COMPANY_FIELD } from './membership';

export interface MembershipFields {
  fields: ViewField[];
  /** The security-attribute fields, in view order (Company excluded). */
  attributeFieldIds: string[];
  fieldLabel: (fielddefId: string) => string;
  /** A value's name; the id itself while the lists are still loading. */
  valueName: (fielddefId: string, valueId: string) => string;
  loading: boolean;
}

export const useMembershipFields = (
  tenantId: string | undefined,
  modelId: string = MEMBERSHIP_MODEL_ID
): MembershipFields => {
  const { data: view, isLoading } = useQuery<ViewConfiguration>({
    queryKey: ['metadata-view', modelId, 'edit-modal', tenantId],
    queryFn: async () =>
      (await metadataService.getModelView(
        modelId,
        tenantId as string,
        'edit-modal'
      )) as unknown as ViewConfiguration,
    staleTime: 5 * 60 * 1000,
    enabled: !!tenantId,
  });

  const fields = useMemo(() => view?.fields ?? [], [view]);
  const ancestors = useHierarchyAncestorFields(fields, tenantId);
  const lookupFields = useMemo(
    () => Array.from(new Set([...fields.map((f) => f.fielddef_id), ...ancestors])),
    [fields, ancestors]
  );
  const { getValue, isLoading: valuesLoading } = useMDMLookup(lookupFields, tenantId || '');

  const fieldLabel = useCallback(
    (fielddefId: string) => {
      const field = fields.find((f) => f.fielddef_id === fielddefId);
      return field?.label_override || field?.field_definition.label || fielddefId;
    },
    [fields]
  );
  const valueName = useCallback(
    (fielddefId: string, valueId: string) => getValue(fielddefId, valueId) || valueId,
    [getValue]
  );
  const attributeFieldIds = useMemo(
    () => fields.map((f) => f.fielddef_id).filter((id) => id !== COMPANY_FIELD),
    [fields]
  );

  return { fields, attributeFieldIds, fieldLabel, valueName, loading: isLoading || valuesLoading };
};

export default useMembershipFields;
